/**
 * Analytics Showcase Page
 * Deep dive into Studio Intelligence Hub features
 * Solis Games - Phase 2.2
 */

import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Icon from '../../components/AppIcon';

const AnalyticsShowcase = () => {
  const [activeDemo, setActiveDemo] = useState('cohorts');

  // Sample data for visualizations
  const sampleCohortData = [
    { week: '2025-01-13', w0: 500, w1: 72, w2: 58, w4: 42, w8: 35 },
    { week: '2025-01-06', w0: 320, w1: 65, w2: 52, w4: 38, w8: 28 },
    { week: '2024-12-30', w0: 450, w1: 48, w2: 35, w4: 22, w8: 15 },
    { week: '2024-12-23', w0: 380, w1: 55, w2: 42, w4: 28, w8: 18 },
    { week: '2024-12-16', w0: 410, w1: 68, w2: 54, w4: 40, w8: 32 }
  ];

  const sampleJourneyData = [
    { stage: 'Discovered', users: 10000, rate: 100, icon: 'Eye' },
    { stage: 'Interested', users: 6500, rate: 65, icon: 'MousePointer' },
    { stage: 'First Play', users: 4200, rate: 65, icon: 'Play' },
    { stage: 'Return Player', users: 2800, rate: 67, icon: 'RotateCcw' },
    { stage: 'Engaged', users: 1680, rate: 60, icon: 'Heart' },
    { stage: 'Champion', users: 420, rate: 25, icon: 'Award' }
  ];

  const getRetentionColor = (value) => {
    if (value >= 70) return 'bg-green-500';
    if (value >= 50) return 'bg-yellow-500';
    if (value >= 30) return 'bg-orange-500';
    return 'bg-red-500';
  };

  const getConversionColor = (rate) => {
    if (rate >= 70) return 'text-green-500';
    if (rate >= 50) return 'text-yellow-500';
    if (rate >= 30) return 'text-orange-500';
    return 'text-red-500';
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Studio Intelligence Hub - Advanced Analytics | Solis Games</title>
        <meta
          name="description"
          content="See why Solis Games offers the most advanced analytics for game developers. Cohort analysis, competitive benchmarking, player journey mapping - analytics intelligence no other platform provides."
        />
      </Helmet>

      <Header />

      <main className="pt-16 pb-20">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-primary/5 via-purple-500/5 to-background py-20">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-500/10 border border-purple-500/20 rounded-full mb-6">
                <Icon name="Sparkles" size={18} className="text-purple-500" />
                <span className="text-sm font-semibold text-purple-500">EXCLUSIVE TO SOLIS GAMES</span>
              </div>

              <h1 className="text-5xl md:text-6xl font-bold text-foreground mb-6">
                Studio Intelligence Hub
              </h1>

              <p className="text-xl text-muted-foreground mb-8 leading-relaxed">
                The analytics intelligence that <span className="text-foreground font-semibold">Steam, Epic, and itch.io</span> don't offer.
                Make better games with insights other platforms can't provide.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/user-registration"
                  className="inline-flex items-center px-8 py-3 bg-primary text-primary-foreground rounded-lg font-semibold hover:bg-primary/90 transition-colors"
                >
                  <Icon name="Rocket" size={20} className="mr-2" />
                  Start Building
                </Link>
                <Link
                  to="#comparison"
                  className="inline-flex items-center px-8 py-3 border border-border text-foreground rounded-lg font-semibold hover:bg-muted transition-colors"
                >
                  <Icon name="BarChart3" size={20} className="mr-2" />
                  Compare Platforms
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Key Differentiators */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                Three Features That Change Everything
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Analytics intelligence you simply can't get anywhere else
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {/* Cohort Retention */}
              <div className="bg-card border border-border rounded-xl p-6 hover:border-primary/50 transition-all">
                <div className="w-12 h-12 bg-green-500/10 rounded-lg flex items-center justify-center mb-4">
                  <Icon name="Calendar" size={24} className="text-green-500" />
                </div>
                <h3 className="text-xl font-bold text-foreground mb-2">
                  Cohort Retention Heat Maps
                </h3>
                <p className="text-muted-foreground mb-4">
                  Visual week-by-week retention analysis with color-coding. See patterns at a glance.
                </p>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-start">
                    <Icon name="Check" size={16} className="text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Week 0, 1, 2, 4, 8, 12 tracking</span>
                  </li>
                  <li className="flex items-start">
                    <Icon name="Check" size={16} className="text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Color-coded performance</span>
                  </li>
                  <li className="flex items-start">
                    <Icon name="Check" size={16} className="text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Trend line visualizations</span>
                  </li>
                </ul>
              </div>

              {/* Competitive Benchmarking */}
              <div className="bg-card border border-border rounded-xl p-6 hover:border-primary/50 transition-all">
                <div className="w-12 h-12 bg-blue-500/10 rounded-lg flex items-center justify-center mb-4">
                  <Icon name="BarChart3" size={24} className="text-blue-500" />
                </div>
                <h3 className="text-xl font-bold text-foreground mb-2">
                  Anonymous Benchmarking
                </h3>
                <p className="text-muted-foreground mb-4">
                  Compare your game vs genre averages. Know your percentile rank.
                </p>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-start">
                    <Icon name="Check" size={16} className="text-blue-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Percentile rankings</span>
                  </li>
                  <li className="flex items-start">
                    <Icon name="Check" size={16} className="text-blue-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Multi-dimensional radar charts</span>
                  </li>
                  <li className="flex items-start">
                    <Icon name="Check" size={16} className="text-blue-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>100% anonymous & secure</span>
                  </li>
                </ul>
              </div>

              {/* Player Journey */}
              <div className="bg-card border border-border rounded-xl p-6 hover:border-primary/50 transition-all">
                <div className="w-12 h-12 bg-purple-500/10 rounded-lg flex items-center justify-center mb-4">
                  <Icon name="TrendingUp" size={24} className="text-purple-500" />
                </div>
                <h3 className="text-xl font-bold text-foreground mb-2">
                  Player Journey Mapping
                </h3>
                <p className="text-muted-foreground mb-4">
                  6-stage lifecycle funnel showing exactly where players drop off.
                </p>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-start">
                    <Icon name="Check" size={16} className="text-purple-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Complete lifecycle tracking</span>
                  </li>
                  <li className="flex items-start">
                    <Icon name="Check" size={16} className="text-purple-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Drop-off analysis with warnings</span>
                  </li>
                  <li className="flex items-start">
                    <Icon name="Check" size={16} className="text-purple-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Actionable recommendations</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Interactive Demo Section */}
        <section className="py-16">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                See It In Action
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Interactive examples with sample data
              </p>
            </div>

            {/* Tab Navigation */}
            <div className="flex justify-center gap-2 mb-8 overflow-x-auto">
              <button
                onClick={() => setActiveDemo('cohorts')}
                className={`px-6 py-3 rounded-lg font-medium transition-colors whitespace-nowrap ${
                  activeDemo === 'cohorts'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-card text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name="Calendar" size={18} className="inline mr-2" />
                Cohort Analysis
              </button>
              <button
                onClick={() => setActiveDemo('journey')}
                className={`px-6 py-3 rounded-lg font-medium transition-colors whitespace-nowrap ${
                  activeDemo === 'journey'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-card text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name="TrendingUp" size={18} className="inline mr-2" />
                Player Journey
              </button>
              <button
                onClick={() => setActiveDemo('benchmarks')}
                className={`px-6 py-3 rounded-lg font-medium transition-colors whitespace-nowrap ${
                  activeDemo === 'benchmarks'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-card text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name="BarChart3" size={18} className="inline mr-2" />
                Benchmarks
              </button>
            </div>

            {/* Demo Content */}
            <div className="max-w-5xl mx-auto">
              {/* Cohort Heat Map Demo */}
              {activeDemo === 'cohorts' && (
                <div className="bg-card border border-border rounded-xl p-8">
                  <div className="mb-6">
                    <h3 className="text-2xl font-bold text-foreground mb-2">Cohort Retention Heat Map</h3>
                    <p className="text-muted-foreground">
                      Color-coded week-by-week retention rates. Green = excellent, Red = needs work.
                    </p>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-border">
                          <th className="text-left p-3 text-sm font-semibold text-muted-foreground">Cohort Week</th>
                          <th className="text-center p-3 text-sm font-semibold text-muted-foreground">Week 0</th>
                          <th className="text-center p-3 text-sm font-semibold text-muted-foreground">Week 1</th>
                          <th className="text-center p-3 text-sm font-semibold text-muted-foreground">Week 2</th>
                          <th className="text-center p-3 text-sm font-semibold text-muted-foreground">Week 4</th>
                          <th className="text-center p-3 text-sm font-semibold text-muted-foreground">Week 8</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sampleCohortData.map((cohort, idx) => (
                          <tr key={idx} className="border-b border-border/50">
                            <td className="p-3 text-sm font-mono text-foreground">{cohort.week}</td>
                            <td className="p-3 text-center">
                              <div className="inline-flex items-center justify-center px-3 py-1 rounded bg-primary/10 text-primary font-semibold">
                                {cohort.w0}
                              </div>
                            </td>
                            <td className="p-3 text-center">
                              <div className={`inline-flex items-center justify-center px-3 py-1 rounded ${getRetentionColor(cohort.w1)} text-white font-semibold`}>
                                {cohort.w1}%
                              </div>
                            </td>
                            <td className="p-3 text-center">
                              <div className={`inline-flex items-center justify-center px-3 py-1 rounded ${getRetentionColor(cohort.w2)} text-white font-semibold`}>
                                {cohort.w2}%
                              </div>
                            </td>
                            <td className="p-3 text-center">
                              <div className={`inline-flex items-center justify-center px-3 py-1 rounded ${getRetentionColor(cohort.w4)} text-white font-semibold`}>
                                {cohort.w4}%
                              </div>
                            </td>
                            <td className="p-3 text-center">
                              <div className={`inline-flex items-center justify-center px-3 py-1 rounded ${getRetentionColor(cohort.w8)} text-white font-semibold`}>
                                {cohort.w8}%
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="mt-6 p-4 bg-muted/50 rounded-lg">
                    <div className="flex items-start gap-2">
                      <Icon name="Lightbulb" size={18} className="text-yellow-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-semibold text-foreground mb-1">Insight</p>
                        <p className="text-sm text-muted-foreground">
                          Recent cohorts (Jan 2025) show 72% week 1 retention vs 48% in Dec 2024.
                          Your recent improvements are working! 📈
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Player Journey Demo */}
              {activeDemo === 'journey' && (
                <div className="bg-card border border-border rounded-xl p-8">
                  <div className="mb-6">
                    <h3 className="text-2xl font-bold text-foreground mb-2">Player Journey Funnel</h3>
                    <p className="text-muted-foreground">
                      Track players from discovery to championship. See exactly where drop-offs happen.
                    </p>
                  </div>

                  <div className="space-y-4">
                    {sampleJourneyData.map((stage, idx) => {
                      const prevUsers = idx > 0 ? sampleJourneyData[idx - 1].users : stage.users;
                      const widthPercent = (stage.users / sampleJourneyData[0].users) * 100;
                      const dropOffRate = idx > 0 ? ((prevUsers - stage.users) / prevUsers * 100).toFixed(0) : 0;
                      const isCriticalDropOff = dropOffRate > 50;

                      return (
                        <div key={idx}>
                          <div
                            className="mx-auto transition-all hover:scale-[1.02]"
                            style={{ width: `${Math.max(widthPercent, 20)}%` }}
                          >
                            <div className="bg-gradient-to-r from-primary/10 to-primary/5 border-2 border-primary/20 rounded-lg p-4">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <div className="p-2 rounded-lg bg-primary shadow-lg">
                                    <Icon name={stage.icon} size={20} className="text-primary-foreground" />
                                  </div>
                                  <div>
                                    <div className="font-semibold text-foreground">{stage.stage}</div>
                                    {idx > 0 && (
                                      <div className={`text-xs ${getConversionColor(stage.rate)}`}>
                                        {stage.rate}% conversion
                                      </div>
                                    )}
                                  </div>
                                </div>
                                <div className="text-right">
                                  <div className="text-2xl font-bold text-foreground">
                                    {stage.users.toLocaleString()}
                                  </div>
                                  <div className="text-xs text-muted-foreground">players</div>
                                </div>
                              </div>

                              {isCriticalDropOff && (
                                <div className="mt-3 p-2 bg-red-500/10 border border-red-500/20 rounded">
                                  <div className="flex items-center gap-2">
                                    <Icon name="AlertTriangle" size={14} className="text-red-500" />
                                    <span className="text-xs text-red-500 font-semibold">
                                      High drop-off: {dropOffRate}% lost
                                    </span>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>

                          {idx < sampleJourneyData.length - 1 && (
                            <div className="flex justify-center my-2">
                              <Icon name="ChevronDown" size={24} className="text-muted-foreground" />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  <div className="mt-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
                    <div className="flex items-start gap-2">
                      <Icon name="AlertTriangle" size={18} className="text-red-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-semibold text-foreground mb-1">Critical Drop-Off Detected</p>
                        <p className="text-sm text-muted-foreground mb-2">
                          Only 25% of engaged players become champions. This is below the 40% genre average.
                        </p>
                        <p className="text-sm font-semibold text-foreground">
                          💡 Recommendation: Add more milestone rewards and social features to encourage long-term play.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Benchmarks Demo */}
              {activeDemo === 'benchmarks' && (
                <div className="bg-card border border-border rounded-xl p-8">
                  <div className="mb-6">
                    <h3 className="text-2xl font-bold text-foreground mb-2">Competitive Benchmarking</h3>
                    <p className="text-muted-foreground">
                      See how your game stacks up against others in your genre.
                    </p>
                  </div>

                  {/* Percentile Rank Banner */}
                  <div className="bg-gradient-to-br from-green-500/20 to-emerald-500/20 border-l-4 border-green-500 rounded-lg p-6 mb-6">
                    <div className="flex items-center gap-4">
                      <div className="p-4 rounded-full bg-card border-2 border-green-500">
                        <Icon name="Trophy" size={32} className="text-green-500" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-baseline gap-2">
                          <span className="text-4xl font-bold text-green-500">75th</span>
                          <span className="text-lg text-foreground">Percentile</span>
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                          Your game ranks better than <span className="font-semibold text-foreground">75%</span> of puzzle games on the platform
                        </p>
                      </div>
                      <div className="text-right">
                        <div className="text-3xl font-bold text-foreground">85</div>
                        <div className="text-xs text-muted-foreground">Engagement Score</div>
                      </div>
                    </div>
                  </div>

                  {/* Metrics Comparison */}
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-muted/30 border border-border rounded-lg p-4">
                      <div className="text-xs text-muted-foreground mb-2">Engagement Score</div>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-blue-500 font-medium">You</span>
                          <span className="text-lg font-bold text-foreground">85</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-orange-500 font-medium">Genre</span>
                          <span className="text-sm text-muted-foreground">68</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-purple-500 font-medium">Platform</span>
                          <span className="text-sm text-muted-foreground">52</span>
                        </div>
                      </div>
                    </div>

                    <div className="bg-muted/30 border border-border rounded-lg p-4">
                      <div className="text-xs text-muted-foreground mb-2">Session Length (min)</div>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-blue-500 font-medium">You</span>
                          <span className="text-lg font-bold text-foreground">28.5</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-orange-500 font-medium">Genre</span>
                          <span className="text-sm text-muted-foreground">32.1</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-purple-500 font-medium">Platform</span>
                          <span className="text-sm text-muted-foreground">24.8</span>
                        </div>
                      </div>
                    </div>

                    <div className="bg-muted/30 border border-border rounded-lg p-4">
                      <div className="text-xs text-muted-foreground mb-2">Unique Players</div>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-blue-500 font-medium">You</span>
                          <span className="text-lg font-bold text-foreground">12.5K</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-orange-500 font-medium">Genre</span>
                          <span className="text-sm text-muted-foreground">8.2K</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-purple-500 font-medium">Platform</span>
                          <span className="text-sm text-muted-foreground">5.1K</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* AI Insights */}
                  <div className="mt-6 space-y-3">
                    <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
                      <div className="flex items-start gap-3">
                        <Icon name="CheckCircle" size={20} className="text-green-500 flex-shrink-0 mt-0.5" />
                        <div>
                          <div className="text-sm font-semibold text-foreground mb-1">Above Average Engagement</div>
                          <div className="text-sm text-muted-foreground">
                            Your engagement score is 25% higher than genre average - keep doing what you're doing!
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                      <div className="flex items-start gap-3">
                        <Icon name="AlertCircle" size={20} className="text-yellow-500 flex-shrink-0 mt-0.5" />
                        <div>
                          <div className="text-sm font-semibold text-foreground mb-1">Session Length Below Genre</div>
                          <div className="text-sm text-muted-foreground">
                            Your session length is 11% below genre average. Consider improving onboarding flow or adding mid-session hooks.
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 p-4 bg-muted/50 rounded-lg">
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Icon name="Shield" size={14} />
                      <span>All comparisons are anonymous. Your competitors cannot see your specific metrics.</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Platform Comparison Teaser */}
        <section id="comparison" className="py-16 bg-muted/30">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                Why Developers Choose Solis
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Compare what you get with Solis vs other platforms
              </p>
            </div>

            <div className="max-w-4xl mx-auto bg-card border border-border rounded-xl p-8">
              <div className="grid grid-cols-5 gap-4 text-center">
                <div className="text-sm font-semibold text-muted-foreground">Feature</div>
                <div className="text-sm font-semibold text-muted-foreground">Steam</div>
                <div className="text-sm font-semibold text-muted-foreground">Epic</div>
                <div className="text-sm font-semibold text-muted-foreground">itch.io</div>
                <div className="text-sm font-semibold text-primary">Solis</div>
              </div>

              <div className="mt-4 space-y-3">
                <div className="grid grid-cols-5 gap-4 items-center py-3 border-t border-border">
                  <div className="text-sm text-foreground">Cohort Heat Maps</div>
                  <div className="text-center"><Icon name="X" size={18} className="text-red-500 mx-auto" /></div>
                  <div className="text-center"><Icon name="X" size={18} className="text-red-500 mx-auto" /></div>
                  <div className="text-center"><Icon name="X" size={18} className="text-red-500 mx-auto" /></div>
                  <div className="text-center"><Icon name="Check" size={18} className="text-green-500 mx-auto" /></div>
                </div>

                <div className="grid grid-cols-5 gap-4 items-center py-3 border-t border-border">
                  <div className="text-sm text-foreground">Competitive Benchmarking</div>
                  <div className="text-center"><Icon name="X" size={18} className="text-red-500 mx-auto" /></div>
                  <div className="text-center"><Icon name="X" size={18} className="text-red-500 mx-auto" /></div>
                  <div className="text-center"><Icon name="X" size={18} className="text-red-500 mx-auto" /></div>
                  <div className="text-center"><Icon name="Check" size={18} className="text-green-500 mx-auto" /></div>
                </div>

                <div className="grid grid-cols-5 gap-4 items-center py-3 border-t border-border">
                  <div className="text-sm text-foreground">Player Journey Funnel</div>
                  <div className="text-center"><Icon name="X" size={18} className="text-red-500 mx-auto" /></div>
                  <div className="text-center"><Icon name="X" size={18} className="text-red-500 mx-auto" /></div>
                  <div className="text-center"><Icon name="X" size={18} className="text-red-500 mx-auto" /></div>
                  <div className="text-center"><Icon name="Check" size={18} className="text-green-500 mx-auto" /></div>
                </div>

                <div className="grid grid-cols-5 gap-4 items-center py-3 border-t border-border">
                  <div className="text-sm text-foreground">Quality Scoring</div>
                  <div className="text-center"><Icon name="X" size={18} className="text-red-500 mx-auto" /></div>
                  <div className="text-center"><Icon name="X" size={18} className="text-red-500 mx-auto" /></div>
                  <div className="text-center"><Icon name="X" size={18} className="text-red-500 mx-auto" /></div>
                  <div className="text-center"><Icon name="Check" size={18} className="text-green-500 mx-auto" /></div>
                </div>

                <div className="grid grid-cols-5 gap-4 items-center py-3 border-t border-border">
                  <div className="text-sm text-foreground">AI-Powered Insights</div>
                  <div className="text-center"><Icon name="X" size={18} className="text-red-500 mx-auto" /></div>
                  <div className="text-center"><Icon name="X" size={18} className="text-red-500 mx-auto" /></div>
                  <div className="text-center"><Icon name="X" size={18} className="text-red-500 mx-auto" /></div>
                  <div className="text-center"><Icon name="Check" size={18} className="text-green-500 mx-auto" /></div>
                </div>
              </div>

              <div className="mt-6 text-center">
                <Link
                  to="/platform-comparison"
                  className="inline-flex items-center text-primary hover:underline font-semibold"
                >
                  See Full Comparison
                  <Icon name="ArrowRight" size={18} className="ml-2" />
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="max-w-3xl mx-auto bg-gradient-to-br from-primary/10 via-purple-500/10 to-background border border-primary/20 rounded-2xl p-12 text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                Ready to Build Smarter Games?
              </h2>
              <p className="text-lg text-muted-foreground mb-8">
                Join studios using data-driven insights to make better games
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/user-registration"
                  className="inline-flex items-center px-8 py-3 bg-primary text-primary-foreground rounded-lg font-semibold hover:bg-primary/90 transition-colors"
                >
                  <Icon name="Rocket" size={20} className="mr-2" />
                  Start Free
                </Link>
                <Link
                  to="/studio-sdk"
                  className="inline-flex items-center px-8 py-3 border border-border text-foreground rounded-lg font-semibold hover:bg-muted transition-colors"
                >
                  <Icon name="BookOpen" size={20} className="mr-2" />
                  View Documentation
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default AnalyticsShowcase;
